import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type PropsWithChildren } from "react";
import { useLocation } from "react-router";
import { storage } from "@/lib/local-storage";
import type { UserType } from "@/types/account";
import { useChatStream } from "@/hooks/use-chat-stream";
import { apiGet, apiPost } from "@/lib/api-client";
import { useMe } from "@/hooks/api/use-auth";
import type { UIResource } from "./ui-resource-renderer";

export type ChatMessage = {
    id: string;
    role: "user" | "assistant";
    content: string;
    timestamp: number;
    uiResources?: UIResource[];
    isStreaming?: boolean;
};

export type ThinkingStep = {
    id: string;
    step_type: "thinking" | "tool_call" | "rag_search" | "delegate" | "response" | "compaction";
    content: string;
    timestamp: string;
    agent_name: string;
    metadata?: Record<string, any>;
};

type Point = { x: number; y: number };

export type ChatHistory = {
    id: string;
    timestamp: number;
    messages: ChatMessage[];
    formData?: Record<string, any>;
    completed?: boolean;
    wizardType?: "container" | "order" | "quote_request" | "quote_response";
    quoteRequestId?: string; // Per FREIGHT_FORWARDER
    title?: string; // Titolo personalizzato della chat
};

export type ChatSession = {
    id: string;
    backendConversationId: string | null;
    title: string | null;
    messages: ChatMessage[];
    createdAt: number;
    lastMessageAt: number | null;
    isLoaded?: boolean;
};

export type ChatTab = {
    id: string;
    title: string | null;
    backendConversationId: string | null;
};

type ChatAIContextValue = {
    isDrawerOpen: boolean;
    openDrawer: () => void;
    closeDrawer: () => void;
    toggleDrawer: () => void;

    isPromptOpen: boolean;
    promptPosition: Point | null;
    openFloatingPrompt: (pos: Point) => void;
    closeFloatingPrompt: () => void;

    messages: ChatMessage[];
    appendMessage: (m: Omit<ChatMessage, "id" | "timestamp"> & Partial<Pick<ChatMessage, "timestamp">>) => void;
    submitPrompt: (text: string, files?: File[], documentIds?: string[]) => void;

    // typing state for assistant placeholder
    isTyping: boolean;
    startTyping: () => void;
    stopTyping: () => void;

    // Chat history
    currentChatId: string | null;
    isNewChat: boolean;
    createNewChat: () => void;
    saveChatHistory: () => void;
    loadLastChat: () => ChatHistory | null;
    getAllChats: () => ChatHistory[];
    loadChat: (chatId: string) => void;
    deleteChat: (chatId: string) => void;
    updateChatTitle: (chatId: string, title: string) => void;

    // Multi-chat tabs
    activeChats: Map<string, ChatSession>;
    activeChatId: string | null;
    chatTabs: ChatTab[];
    switchChat: (chatId: string) => void;
    closeChatTab: (chatId: string) => void;
    loadLatestChat: () => Promise<void>;
    openConversation: (conversationId: string, title?: string | null) => void;

    // History view
    isHistoryView: boolean;
    toggleHistoryView: () => void;

    // Wizard state
    activeWizard: "container" | "order" | "quote_request" | "quote_response" | null;
    setActiveWizard: (wizard: "container" | "order" | "quote_request" | "quote_response" | null) => void;
    wizardFormData: Record<string, any>;
    setWizardFormData: (data: Record<string, any>) => void;

    // Quote context for FREIGHT_FORWARDER
    quoteRequestId: string | null;
    setQuoteRequestId: (id: string | null) => void;

    // Reasoning states (for streaming)
    currentAgent: string | null;
    thinkingSteps: ThinkingStep[];
    // Get thinking steps for a specific user message ID
    getThinkingStepsForMessage: (userMessageId: string) => ThinkingStep[];
    activeToolCalls: Array<{
        id: string;
        tool_name: string;
        input?: any;
        output?: any;
        agent_name: string;
        timestamp: string;
    }>;
    agentDelegations: Array<{
        id: string;
        from_agent: string;
        to_agent: string;
        reason: string;
        timestamp: string;
    }>;
    isStreaming: boolean;
    streamError: Error | null;
    stop: () => void;
};

const ChatAIContext = createContext<ChatAIContextValue | null>(null);

export const useChatAI = (): ChatAIContextValue => {
    const ctx = useContext(ChatAIContext);
    if (!ctx) {
        throw new Error("useChatAI must be used within ChatAIProvider");
    }
    return ctx;
};

function generateId(): string {
    // simple unique id for UI purposes
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function getStorageKey(): string {
    const accountId = storage.getItem('accountId') || '1';
    const userType = storage.getItem('userType') as UserType | null || 'SHIPPER';
    return `aiChatHistory_${accountId}_${userType}`;
}

function getTabsStorageKey(): string {
    const accountId = storage.getItem('accountId') || '1';
    const userType = storage.getItem('userType') as UserType | null || 'SHIPPER';
    return `aiChatTabs_${accountId}_${userType}`;
}

function loadChatHistory(): ChatHistory[] {
    try {
        const key = getStorageKey();
        const stored = storage.getItem(key);
        if (stored) {
            const parsed = JSON.parse(stored);
            // Convert timestamp dates back to Date objects
            return parsed.map((chat: any) => ({
                ...chat,
                timestamp: new Date(chat.timestamp),
                messages: chat.messages.map((msg: any) => ({
                    ...msg,
                    timestamp: new Date(msg.timestamp),
                })),
            }));
        }
    } catch (error) {
        console.error('Error loading chat history:', error);
    }
    return [];
}

function saveChatHistory(history: ChatHistory[]): void {
    try {
        const key = getStorageKey();
        storage.setItem(key, JSON.stringify(history));
    } catch (error) {
        console.error('Error saving chat history:', error);
    }
}

export const ChatAIProvider = ({ children }: PropsWithChildren) => {
    const location = useLocation();
    const { data: currentUser } = useMe();
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isPromptOpen, setIsPromptOpen] = useState(false);
    const [promptPosition, setPromptPosition] = useState<Point | null>(null);
    const [currentChatId, setCurrentChatId] = useState<string | null>(null);
    const [activeChats, setActiveChats] = useState<Map<string, ChatSession>>(new Map());
    const [chatTabs, setChatTabs] = useState<ChatTab[]>([]);
    const [activeWizard, setActiveWizard] = useState<"container" | "order" | "quote_request" | "quote_response" | null>(null);
    const [wizardFormData, setWizardFormData] = useState<Record<string, any>>({});
    const [quoteRequestId, setQuoteRequestId] = useState<string | null>(null);
    const [isHistoryView, setIsHistoryView] = useState(false);
    const [historyVersion, setHistoryVersion] = useState(0); // Force re-render when history changes
    const [backendConversationId, setBackendConversationId] = useState<string | null>(null);
    // Use ref to track conversation ID for immediate access (React state updates are async)
    const backendConversationIdRef = useRef<string | null>(null);
    // Map to track thinking steps per user message ID
    const thinkingStepsMapRef = useRef<Map<string, ThinkingStep[]>>(new Map());
    const lastUserMessageIdRef = useRef<string | null>(null);
    // State to force re-render when thinking steps map changes
    const [thinkingStepsMapVersion, setThinkingStepsMapVersion] = useState(0);
    // Map to track UI resources per assistant message ID
    const uiResourcesMapRef = useRef<Map<string, UIResource[]>>(new Map());
    const lastAssistantMessageIdRef = useRef<string | null>(null);
    // State to force re-render when UI resources map changes
    const [uiResourcesMapVersion, setUIResourcesMapVersion] = useState(0);
    // Files to attach to the next message - use ref for immediate access
    const pendingFilesRef = useRef<File[]>([]);

    // Document IDs to include in the next message - use ref for immediate access
    const pendingDocumentIdsRef = useRef<string[]>([]);
    // Track if loadLatestChat has been called to avoid multiple calls
    const loadLatestChatCalledRef = useRef<boolean>(false);

    // Create or get backend conversation ID
    const ensureConversation = useCallback(async (forceNew = false): Promise<string | null> => {
        if (!forceNew && backendConversationIdRef.current) return backendConversationIdRef.current;

        // Verifica che l'utente sia autenticato prima di creare la conversazione
        if (!currentUser) {
            console.warn("Cannot create conversation: user is not authenticated");
            return null;
        }

        try {
            const conversation = await apiPost<{ conversation_id: string }>("/conversations", {
                title: null,
                agent_id: null, // Use default agent
            });
            const convId = conversation.conversation_id;
            backendConversationIdRef.current = convId; // Update ref immediately
            setBackendConversationId(convId); // Update state for re-renders
            return convId;
        } catch (error) {
            console.error("Failed to create conversation:", error);
            // Non usare un ID temporaneo se l'utente non è autenticato
            // La chiamata fallirà quando si tenta di inviare un messaggio, ma è meglio così
            return null;
        }
    }, [currentUser]);

    // Use chat stream hook with dynamic conversation ID getter
    // This ensures we always use the latest backendConversationId when available
    // Use ref for immediate access (React state updates are async)
    const {
        messages: streamMessages,
        input: _input,
        setMessages: setStreamMessages,
        append: streamAppend,
        isLoading,
        stop,
        reload: _reload,
        currentAgent,
        thinkingSteps,
        activeToolCalls,
        agentDelegations,
        uiResources,
        isStreaming,
        streamError,
    } = useChatStream({
        conversationId: () => backendConversationIdRef.current,
        onError: (error) => {
            console.error("Chat stream error:", error);
        },
        getFiles: () => {
            // Use ref for immediate access (state updates are async)
            const files = pendingFilesRef.current.length > 0 ? [...pendingFilesRef.current] : null;

            if (files) {
                // Clear files after returning them (they'll be used in the request)
                pendingFilesRef.current = [];
            }
            return files;
        },
        getDocumentIds: () => {
            // Use ref for immediate access (state updates are async)
            const documentIds = pendingDocumentIdsRef.current.length > 0 ? [...pendingDocumentIdsRef.current] : null;

            if (documentIds) {
                // Clear document IDs after returning them (they'll be used in the request)
                pendingDocumentIdsRef.current = [];
            }

            return documentIds;
        },
        onConversationTitle: (conversationId: string, title: string) => {
            const matchingTab = chatTabs.find((tab) => tab.backendConversationId === conversationId);
            if (matchingTab) {
                updateChatTitle(matchingTab.id, title);
            }
        },
    });

    // Track the last user message ID when a new user message is added
    useEffect(() => {
        const lastUserMessage = streamMessages
            .slice()
            .reverse()
            .find(msg => msg.role === "user");
        if (lastUserMessage && lastUserMessage.id !== lastUserMessageIdRef.current) {
            // New user message detected - initialize thinking steps map for this message
            lastUserMessageIdRef.current = lastUserMessage.id;
            thinkingStepsMapRef.current.set(lastUserMessage.id, []);
            // Force re-render by updating version
            setThinkingStepsMapVersion(prev => prev + 1);
        }
    }, [streamMessages]);

    // Update thinking steps map when thinking steps change
    useEffect(() => {
        // If lastUserMessageIdRef is not set but we have thinking steps, find the last user message
        if (!lastUserMessageIdRef.current && thinkingSteps.length > 0) {
            const lastUserMessage = streamMessages
                .slice()
                .reverse()
                .find(msg => msg.role === "user");
            if (lastUserMessage) {
                lastUserMessageIdRef.current = lastUserMessage.id;
                thinkingStepsMapRef.current.set(lastUserMessage.id, []);
                // Force re-render by updating version
                setThinkingStepsMapVersion(prev => prev + 1);
            }
        }

        if (lastUserMessageIdRef.current && thinkingSteps.length > 0) {
            const currentSteps = thinkingStepsMapRef.current.get(lastUserMessageIdRef.current) || [];
            // Merge with existing steps, avoiding duplicates
            const existingIds = new Set(currentSteps.map(s => s.id));
            const newSteps = thinkingSteps.filter(s => !existingIds.has(s.id));
            if (newSteps.length > 0) {
                thinkingStepsMapRef.current.set(
                    lastUserMessageIdRef.current,
                    [...currentSteps, ...newSteps]
                );
                // Force re-render by updating version
                setThinkingStepsMapVersion(prev => prev + 1);
            }
        }
    }, [thinkingSteps, streamMessages]);

    // Save thinking steps when streaming ends to ensure they persist
    useEffect(() => {
        if (!isStreaming && lastUserMessageIdRef.current && thinkingSteps.length > 0) {
            // Streaming ended - ensure all thinking steps are saved
            const currentSteps = thinkingStepsMapRef.current.get(lastUserMessageIdRef.current) || [];
            const existingIds = new Set(currentSteps.map(s => s.id));
            const newSteps = thinkingSteps.filter(s => !existingIds.has(s.id));
            if (newSteps.length > 0) {
                thinkingStepsMapRef.current.set(
                    lastUserMessageIdRef.current,
                    [...currentSteps, ...newSteps]
                );
                // Force re-render by updating version
                setThinkingStepsMapVersion(prev => prev + 1);
            }
        }
    }, [isStreaming, thinkingSteps]);

    // Save UI resources when streaming ends to ensure they persist
    useEffect(() => {
        if (!isStreaming && lastAssistantMessageIdRef.current && uiResources.length > 0) {
            // Check if these UI resources already belong to a previous message
            // by checking all previous assistant messages in the map
            let belongsToPreviousMessage = false;
            for (const [messageId, resources] of uiResourcesMapRef.current.entries()) {
                if (messageId !== lastAssistantMessageIdRef.current) {
                    const existingUris = new Set(resources.map(r => r.uri));
                    const allUrisExist = uiResources.every(r => existingUris.has(r.uri));
                    if (allUrisExist && uiResources.length > 0) {
                        belongsToPreviousMessage = true;
                        break;
                    }
                }
            }

            if (belongsToPreviousMessage) {
                // These UI resources belong to a previous message, don't save them to the current message
                return;
            }

            // Streaming ended - ensure all UI resources are saved
            const currentResources = uiResourcesMapRef.current.get(lastAssistantMessageIdRef.current) || [];
            const existingUris = new Set(currentResources.map(r => r.uri));
            const newResources = uiResources.filter(r => !existingUris.has(r.uri));
            if (newResources.length > 0) {
                uiResourcesMapRef.current.set(
                    lastAssistantMessageIdRef.current,
                    [...currentResources, ...newResources]
                );
                // Force re-render by updating version
                setUIResourcesMapVersion(prev => prev + 1);
            }
        }
    }, [isStreaming, uiResources]);

    // Track the last assistant message ID when a new assistant message is added
    useEffect(() => {
        const lastAssistantMessage = streamMessages
            .slice()
            .reverse()
            .find(msg => msg.role === "assistant");
        if (lastAssistantMessage && lastAssistantMessage.id !== lastAssistantMessageIdRef.current) {
            // New assistant message detected
            // If there are UI resources present, they belong to the NEW message, not the previous one
            // because the new message was just created and UI resources arrive after message creation
            const previousMessageId = lastAssistantMessageIdRef.current;

            // Update the current message ID first
            lastAssistantMessageIdRef.current = lastAssistantMessage.id;

            // Initialize UI resources map for new message with any current UI resources
            // These UI resources belong to the new message, not the previous one
            if (uiResources.length > 0) {
                // Check if these UI resources already belong to a previous message
                let belongsToPreviousMessage = false;
                if (previousMessageId) {
                    const previousResources = uiResourcesMapRef.current.get(previousMessageId) || [];
                    const previousUris = new Set(previousResources.map(r => r.uri));
                    const allUrisExist = uiResources.every(r => previousUris.has(r.uri));
                    if (allUrisExist && uiResources.length > 0 && previousResources.length > 0) {
                        belongsToPreviousMessage = true;
                    }
                }

                if (!belongsToPreviousMessage) {
                    // These UI resources belong to the new message
                    uiResourcesMapRef.current.set(lastAssistantMessage.id, [...uiResources]);
                } else if (previousMessageId) {
                    // These UI resources belong to the previous message, save them there
                    const currentResources = uiResourcesMapRef.current.get(previousMessageId) || [];
                    const existingUris = new Set(currentResources.map(r => r.uri));
                    const newResources = uiResources.filter(r => !existingUris.has(r.uri));
                    if (newResources.length > 0) {
                        uiResourcesMapRef.current.set(
                            previousMessageId,
                            [...currentResources, ...newResources]
                        );
                    }
                    // Initialize empty array for new message
                    uiResourcesMapRef.current.set(lastAssistantMessage.id, []);
                } else {
                    // Fallback: if previousMessageId is null but belongsToPreviousMessage is true,
                    // just initialize empty array for new message
                    uiResourcesMapRef.current.set(lastAssistantMessage.id, []);
                }
            } else {
                // No UI resources, just initialize empty array for new message
                uiResourcesMapRef.current.set(lastAssistantMessage.id, []);
            }

            // Force re-render by updating version
            setUIResourcesMapVersion(prev => prev + 1);
            // Reset global UI resources - they will be repopulated for the new message
            // Note: We can't directly reset uiResources here as it's from useChatStream
            // The reset happens in useChatStream when isLoading becomes true
        }
    }, [streamMessages, uiResources]);

    // Update UI resources map when UI resources change
    useEffect(() => {
        // Only process UI resources if we're currently streaming (they belong to the current message)
        // If not streaming, these might be leftover from a previous message
        if (!isStreaming && uiResources.length > 0) {
            return;
        }

        // If lastAssistantMessageIdRef is not set but we have UI resources, find the last assistant message
        if (!lastAssistantMessageIdRef.current && uiResources.length > 0) {
            const lastAssistantMessage = streamMessages
                .slice()
                .reverse()
                .find(msg => msg.role === "assistant");
            if (lastAssistantMessage) {
                lastAssistantMessageIdRef.current = lastAssistantMessage.id;
                uiResourcesMapRef.current.set(lastAssistantMessage.id, []);
            }
        }

        if (lastAssistantMessageIdRef.current && uiResources.length > 0) {
            // If we're currently streaming, UI resources belong to the current message
            // Only check if they belong to a previous message when NOT streaming
            // (to avoid associating leftover UI resources to old messages)
            let belongsToPreviousMessage = false;
            if (!isStreaming) {
                // Check if these UI resources already belong to a previous message
                // by checking all previous assistant messages in the map
                for (const [messageId, resources] of uiResourcesMapRef.current.entries()) {
                    if (messageId !== lastAssistantMessageIdRef.current) {
                        const existingUris = new Set(resources.map(r => r.uri));
                        const allUrisExist = uiResources.every(r => existingUris.has(r.uri));
                        if (allUrisExist && uiResources.length > 0) {
                            belongsToPreviousMessage = true;
                            break;
                        }
                    }
                }
            }

            if (belongsToPreviousMessage) {
                // These UI resources belong to a previous message, don't save them to the current message
                return;
            }

            const currentResources = uiResourcesMapRef.current.get(lastAssistantMessageIdRef.current) || [];
            // Merge with existing resources, avoiding duplicates by URI
            const existingUris = new Set(currentResources.map(r => r.uri));
            const newResources = uiResources.filter(r => !existingUris.has(r.uri));

            if (newResources.length > 0) {
                uiResourcesMapRef.current.set(
                    lastAssistantMessageIdRef.current,
                    [...currentResources, ...newResources]
                );
                // Force re-render by updating version
                setUIResourcesMapVersion(prev => prev + 1);
            }
        }
    }, [uiResources, streamMessages, uiResourcesMapVersion, isStreaming]);

    // Convert stream messages to ChatMessage format
    // Associate UI resources with the correct assistant message from the map
    const messages: ChatMessage[] = useMemo(() => {
        const converted = streamMessages.map((msg, index) => {
            const isLastAssistant =
                msg.role === "assistant" &&
                index === streamMessages.length - 1;

            // Get UI resources for this assistant message from the map
            const messageUIResources = msg.role === "assistant"
                ? (uiResourcesMapRef.current.get(msg.id) || [])
                : undefined;

            return {
                id: msg.id,
                role: msg.role,
                content: msg.content,
                timestamp: Date.now(), // Use current time or extract from message if available
                // Associate UI resources from the map
                uiResources: messageUIResources && messageUIResources.length > 0 ? messageUIResources : undefined,
                isStreaming: isLastAssistant ? isStreaming : false,
            };
        });

        return converted;
    }, [streamMessages, isStreaming, uiResourcesMapVersion]);

    // Function to get thinking steps for a specific user message ID
    // Include thinkingStepsMapVersion in dependencies to force re-render when map changes
    const getThinkingStepsForMessage = useCallback((userMessageId: string): ThinkingStep[] => {
        const steps = thinkingStepsMapRef.current.get(userMessageId) || [];
        return steps;
    }, [thinkingStepsMapVersion]);

    const isTyping = isLoading || isStreaming;

    // Track last mouse position to place floating prompt near cursor
    const lastCursor = useRef<Point>({ x: window.innerWidth / 2, y: window.innerHeight / 2 });

    const isSenderOrReceiver = useMemo(() => {
        const p = location.pathname;
        return p.startsWith("/sender") || p.startsWith("/receiver");
    }, [location.pathname]);

    const isAskEvePage = useMemo(() => {
        return location.pathname.includes("/ask-eve");
    }, [location.pathname]);

    const resetStreamState = useCallback((clearMessages = true) => {
        if (clearMessages) {
            setStreamMessages([]);
        }
        thinkingStepsMapRef.current = new Map();
        setThinkingStepsMapVersion((prev) => prev + 1);
        uiResourcesMapRef.current = new Map();
        setUIResourcesMapVersion((prev) => prev + 1);
    }, [setStreamMessages]);

    const toVercelMessages = useCallback((chatMessages: ChatMessage[]) => {
        return chatMessages.map((msg) => ({
            id: msg.id,
            role: msg.role,
            content: msg.content,
        }));
    }, []);

    const createNewChat = useCallback(async () => {
        const newChatId = generateId();
        const newConvId = await ensureConversation(true);

        if (!newConvId) {
            console.warn("Cannot create new chat: user is not authenticated");
            return;
        }

        const newSession: ChatSession = {
            id: newChatId,
            backendConversationId: newConvId,
            title: null,
            messages: [],
            createdAt: Date.now(),
            lastMessageAt: null,
            isLoaded: true,
        };

        setActiveChats((prev) => {
            const next = new Map(prev);
            next.set(newChatId, newSession);
            return next;
        });

        setChatTabs((prev) => {
            const next = [...prev, { id: newChatId, title: null, backendConversationId: newConvId }];
            return next.slice(-10);
        });

        setCurrentChatId(newChatId);
        backendConversationIdRef.current = newConvId;
        setBackendConversationId(newConvId);
        resetStreamState(true);
        setActiveWizard(null);
        setWizardFormData({});
        setQuoteRequestId(null);
        setIsHistoryView(false);
    }, [ensureConversation, resetStreamState]);

    const loadConversationMessages = useCallback(async (chatId: string, conversationId: string) => {
        try {
            const conversation = await apiGet<{
                conversation_id: string;
                title: string | null;
                started_at: string;
                last_message_at: string | null;
                messages?: Array<{
                    message_id: string;
                    role: "user" | "assistant";
                    content: string;
                    created_at: string;
                }>;
            }>(`/conversations/${conversationId}`, { params: { include_messages: true } });

            const chatMessages: ChatMessage[] = (conversation.messages || []).map((msg) => ({
                id: msg.message_id,
                role: msg.role,
                content: msg.content,
                timestamp: new Date(msg.created_at).getTime(),
            }));

            // Cerca uiResources salvati localmente nello storico per questa conversazione
            const history = loadChatHistory();
            const localChat = history.find((c) => {
                // Cerca per backendConversationId se disponibile, altrimenti per chatId
                const session = activeChats.get(c.id);
                return session?.backendConversationId === conversationId || c.id === chatId;
            });

            // Ripristina uiResources dalla chat locale se disponibile
            uiResourcesMapRef.current = new Map();
            if (localChat) {
                localChat.messages.forEach((msg) => {
                    if (msg.role === "assistant" && msg.uiResources && msg.uiResources.length > 0) {
                        // Associa uiResources al message_id del backend se corrispondono
                        const backendMsg = chatMessages.find((bm) => bm.id === msg.id);
                        if (backendMsg) {
                            uiResourcesMapRef.current.set(backendMsg.id, msg.uiResources);
                        }
                    }
                });
            }
            setUIResourcesMapVersion((prev) => prev + 1);

            setStreamMessages(toVercelMessages(chatMessages));

            setActiveChats((prev) => {
                const next = new Map(prev);
                next.set(chatId, {
                    id: chatId,
                    backendConversationId: conversationId,
                    title: conversation.title,
                    messages: chatMessages,
                    createdAt: new Date(conversation.started_at).getTime(),
                    lastMessageAt: conversation.last_message_at
                        ? new Date(conversation.last_message_at).getTime()
                        : null,
                    isLoaded: true,
                });
                return next;
            });

            setChatTabs((prev) =>
                prev.map((tab) =>
                    tab.id === chatId
                        ? { ...tab, title: conversation.title, backendConversationId: conversationId }
                        : tab
                )
            );
        } catch (error: any) {
            console.error("Failed to load conversation messages:", error);

            // If conversation doesn't exist (404), remove the invalid tab and create a new chat
            const status = error?.status || error?.response?.status;
            if (status === 404) {
                console.warn(`Conversation ${conversationId} not found, removing invalid tab and creating new chat`);

                // Remove the invalid tab
                setChatTabs((prev) => prev.filter((tab) => tab.id !== chatId));
                setActiveChats((prev) => {
                    const next = new Map(prev);
                    next.delete(chatId);
                    return next;
                });

                // If this was the active chat, create a new one or load the latest
                if (currentChatId === chatId) {
                    setCurrentChatId(null);
                    backendConversationIdRef.current = null;
                    setBackendConversationId(null);
                    resetStreamState(true);
                    // Try to load latest chat, or create a new one
                    // Note: loadLatestChat is defined later, so we'll handle this in a useEffect
                    // For now, just create a new chat
                    createNewChat();
                }
            }
        }
    }, [setStreamMessages, toVercelMessages, currentChatId, resetStreamState, createNewChat, activeChats]);

    const loadLatestChat = useCallback(async () => {
        if (!currentUser || !isAskEvePage) return;

        try {
            const conversation = await apiGet<{
                conversation_id: string;
                title: string | null;
                started_at: string;
                last_message_at: string | null;
                messages?: Array<{
                    message_id: string;
                    role: "user" | "assistant";
                    content: string;
                    created_at: string;
                }>;
            }>("/conversations/latest", { params: { include_messages: true } });

            if (!conversation?.conversation_id) {
                createNewChat();
                return;
            }

            const sessionId = generateId();
            const chatMessages: ChatMessage[] = (conversation.messages || []).map((msg) => ({
                id: msg.message_id,
                role: msg.role,
                content: msg.content,
                timestamp: new Date(msg.created_at).getTime(),
            }));

            // Cerca uiResources salvati localmente nello storico
            const history = loadChatHistory();

            const localChat = history.find((c) => {
                const session = activeChats.get(c.id);
                return session?.backendConversationId === conversation.conversation_id;
            });

            // Ripristina uiResources dalla chat locale se disponibile
            uiResourcesMapRef.current = new Map();
            if (localChat) {
                localChat.messages.forEach((msg) => {
                    if (msg.role === "assistant" && msg.uiResources && msg.uiResources.length > 0) {
                        const backendMsg = chatMessages.find((bm) => bm.id === msg.id);
                        if (backendMsg) {
                            uiResourcesMapRef.current.set(backendMsg.id, msg.uiResources);
                        }
                    }
                });
            }
            setUIResourcesMapVersion((prev) => prev + 1);

            setActiveChats((prev) => {
                const next = new Map(prev);
                next.set(sessionId, {
                    id: sessionId,
                    backendConversationId: conversation.conversation_id,
                    title: conversation.title,
                    messages: chatMessages,
                    createdAt: new Date(conversation.started_at).getTime(),
                    lastMessageAt: conversation.last_message_at
                        ? new Date(conversation.last_message_at).getTime()
                        : null,
                    isLoaded: true,
                });
                return next;
            });

            setChatTabs([{
                id: sessionId,
                title: conversation.title,
                backendConversationId: conversation.conversation_id,
            }]);

            setCurrentChatId(sessionId);
            backendConversationIdRef.current = conversation.conversation_id;
            setBackendConversationId(conversation.conversation_id);
            setStreamMessages(toVercelMessages(chatMessages));
            resetStreamState(false);
        } catch (error) {
            console.warn("No latest chat found, creating new chat.");
            createNewChat();
        }
    }, [currentUser, isAskEvePage, setStreamMessages, resetStreamState, toVercelMessages, createNewChat, activeChats]);

    const switchChat = useCallback((chatId: string) => {
        const session = activeChats.get(chatId);
        if (!session) return;

        setCurrentChatId(chatId);
        backendConversationIdRef.current = session.backendConversationId;
        setBackendConversationId(session.backendConversationId);
        resetStreamState(false);

        if (session.isLoaded) {
            setStreamMessages(toVercelMessages(session.messages));
        } else if (session.backendConversationId) {
            loadConversationMessages(chatId, session.backendConversationId);
        } else {
            setStreamMessages([]);
        }

        setActiveWizard(null);
        setWizardFormData({});
        setQuoteRequestId(null);
    }, [activeChats, loadConversationMessages, resetStreamState, setStreamMessages, toVercelMessages]);

    const closeChatTab = useCallback((chatId: string) => {
        if (chatTabs.length <= 1) return;

        setChatTabs((prev) => prev.filter((tab) => tab.id !== chatId));
        setActiveChats((prev) => {
            const next = new Map(prev);
            next.delete(chatId);
            return next;
        });

        if (currentChatId === chatId) {
            const remainingTabs = chatTabs.filter((tab) => tab.id !== chatId);
            const nextTab = remainingTabs[remainingTabs.length - 1];
            if (nextTab) {
                switchChat(nextTab.id);
            } else {
                createNewChat();
            }
        }
    }, [chatTabs, currentChatId, createNewChat, switchChat]);

    const openConversation = useCallback((conversationId: string, title?: string | null) => {
        const existingTab = chatTabs.find((tab) => tab.backendConversationId === conversationId);
        if (existingTab) {
            switchChat(existingTab.id);
            return;
        }

        const newChatId = generateId();
        setChatTabs((prev) => {
            const next = [...prev, { id: newChatId, title: title ?? null, backendConversationId: conversationId }];
            return next.slice(-10);
        });

        setActiveChats((prev) => {
            const next = new Map(prev);
            next.set(newChatId, {
                id: newChatId,
                backendConversationId: conversationId,
                title: title ?? null,
                messages: [],
                createdAt: Date.now(),
                lastMessageAt: null,
                isLoaded: false,
            });
            return next;
        });

        setCurrentChatId(newChatId);
        backendConversationIdRef.current = conversationId;
        setBackendConversationId(conversationId);
        resetStreamState(true);
        loadConversationMessages(newChatId, conversationId);
    }, [chatTabs, loadConversationMessages, resetStreamState, switchChat]);

    const loadLastChat = useCallback((): ChatHistory | null => {
        const history = loadChatHistory();
        if (history.length === 0) return null;
        // Get most recent chat
        const lastChat = history.sort((a: ChatHistory, b: ChatHistory) => b.timestamp - a.timestamp)[0];
        return lastChat;
    }, []);

    const saveChatHistoryToStorage = useCallback(() => {
        if (!currentChatId) return;
        const history = loadChatHistory();
        const existingIndex = history.findIndex((c) => c.id === currentChatId);

        const chatData: ChatHistory = {
            id: currentChatId,
            timestamp: Date.now(),
            messages,
            formData: wizardFormData,
            completed: activeWizard === null && messages.length > 0,
            wizardType: activeWizard || undefined,
            quoteRequestId: quoteRequestId || undefined,
        };
        if (existingIndex !== -1) {
            history[existingIndex] = chatData;
        } else {
            history.push(chatData);
        }
        // Keep only last 50 chats
        const sorted = history.sort((a: ChatHistory, b: ChatHistory) => b.timestamp - a.timestamp).slice(0, 50);
        saveChatHistory(sorted);
    }, [currentChatId, messages, wizardFormData, activeWizard, quoteRequestId]);

    const getAllChats = useCallback((): ChatHistory[] => {
        const history = loadChatHistory();
        // Sort by timestamp, most recent first
        return history.sort((a: ChatHistory, b: ChatHistory) => b.timestamp - a.timestamp);
    }, [historyVersion]); // Re-run when historyVersion changes

    const loadChat = useCallback((chatId: string) => {
        // Save current chat before loading new one
        saveChatHistoryToStorage();

        const history = loadChatHistory();
        const chat = history.find((c) => c.id === chatId);

        if (chat) {
            setCurrentChatId(chat.id);

            // Ripristina uiResources nella Map per ogni messaggio assistant
            uiResourcesMapRef.current = new Map();
            chat.messages.forEach((msg) => {
                if (msg.role === "assistant" && msg.uiResources && msg.uiResources.length > 0) {
                    uiResourcesMapRef.current.set(msg.id, msg.uiResources);
                }
            });

            setUIResourcesMapVersion((prev) => prev + 1);

            // Imposta i messaggi in streamMessages (senza uiResources, già nella Map)
            setStreamMessages(toVercelMessages(chat.messages));

            setActiveWizard(chat.wizardType || null);
            setWizardFormData(chat.formData || {});
            setQuoteRequestId(chat.quoteRequestId || null);
            setIsHistoryView(false); // Torna alla vista chat quando si carica una chat
        } else {
            // If chat not found, create new chat
            createNewChat();
        }
    }, [saveChatHistoryToStorage, createNewChat, setStreamMessages, toVercelMessages]);

    const deleteChat = useCallback((chatId: string) => {
        // Save current chat before deleting
        saveChatHistoryToStorage();

        const history = loadChatHistory();
        const filtered = history.filter((c) => c.id !== chatId);
        saveChatHistory(filtered);
        setHistoryVersion((prev) => prev + 1); // Trigger re-render

        // If deleted chat was current, create new chat or load last one
        if (currentChatId === chatId) {
            const remaining = filtered.sort((a: ChatHistory, b: ChatHistory) => b.timestamp - a.timestamp);
            if (remaining.length > 0) {
                const lastChat = remaining[0];
                setCurrentChatId(lastChat.id);
                // Note: Messages are now managed by useChatStream
                setActiveWizard(lastChat.wizardType || null);
                setWizardFormData(lastChat.formData || {});
                setQuoteRequestId(lastChat.quoteRequestId || null);
            } else {
                createNewChat();
            }
        }
    }, [currentChatId, createNewChat, saveChatHistoryToStorage]);

    const updateChatTitle = useCallback((chatId: string, title: string) => {
        const trimmedTitle = title.trim() || undefined;
        const history = loadChatHistory();
        const chatIndex = history.findIndex((c) => c.id === chatId);
        if (chatIndex !== -1) {
            history[chatIndex] = {
                ...history[chatIndex],
                title: trimmedTitle,
            };
            saveChatHistory(history);
            setHistoryVersion((prev) => prev + 1); // Trigger re-render
        }

        setActiveChats((prev) => {
            const next = new Map(prev);
            const session = next.get(chatId);
            if (session) {
                next.set(chatId, { ...session, title: trimmedTitle ?? null });
            }
            return next;
        });

        setChatTabs((prev) =>
            prev.map((tab) => (tab.id === chatId ? { ...tab, title: trimmedTitle ?? null } : tab))
        );
    }, []);

    const toggleHistoryView = useCallback(() => {
        setIsHistoryView((prev) => !prev);
    }, []);

    const openDrawer = useCallback(() => {
        setIsDrawerOpen(true);
        setIsHistoryView(false); // Di default mostra la chat, non la history
        // Load last chat if exists, otherwise create new
        const lastChat = loadLastChat();
        if (lastChat && lastChat.messages.length > 0) {
            setCurrentChatId(lastChat.id);

            // Ripristina uiResources nella Map
            uiResourcesMapRef.current = new Map();
            lastChat.messages.forEach((msg) => {
                if (msg.role === "assistant" && msg.uiResources && msg.uiResources.length > 0) {
                    uiResourcesMapRef.current.set(msg.id, msg.uiResources);
                }
            });

            setUIResourcesMapVersion((prev) => prev + 1);

            // Imposta i messaggi in streamMessages (senza uiResources, già nella Map)
            setStreamMessages(toVercelMessages(lastChat.messages));

            setActiveWizard(lastChat.wizardType || null);
            setWizardFormData(lastChat.formData || {});
            setQuoteRequestId(lastChat.quoteRequestId || null);
        } else {
            createNewChat();
        }
    }, [loadLastChat, createNewChat, setStreamMessages, toVercelMessages]);

    const closeDrawer = useCallback(() => {
        saveChatHistoryToStorage();
        setIsDrawerOpen(false);
    }, [saveChatHistoryToStorage]);

    const toggleDrawer = useCallback(() => {
        if (isDrawerOpen) {
            closeDrawer();
        } else {
            openDrawer();
        }
    }, [isDrawerOpen, openDrawer, closeDrawer]);

    const openFloatingPrompt = useCallback((pos: Point) => {
        setPromptPosition(pos);
        setIsPromptOpen(true);
    }, []);
    const closeFloatingPrompt = useCallback(() => setIsPromptOpen(false), []);

    const appendMessage = useCallback(async (m: Omit<ChatMessage, "id" | "timestamp"> & Partial<Pick<ChatMessage, "timestamp">>) => {
        // When appending a user message, use append to directly add and send the message
        if (m.role === "user") {
            // Ensure conversation exists before sending message
            if (!backendConversationIdRef.current) {
                const convId = await ensureConversation();
                if (!convId) {
                    console.error("Cannot send message: conversation could not be created");
                    return;
                }
            }

            streamAppend({
                role: "user",
                content: m.content,
            });
        }
    }, [streamAppend, ensureConversation]);

    const submitPrompt = useCallback(
        async (text: string, files?: File[], documentIds?: string[]) => {
            if (!text.trim() && (!files || files.length === 0) && (!documentIds || documentIds.length === 0)) return;
            setIsPromptOpen(false);

            // Set pending files if provided
            if (files && files.length > 0) {
                pendingFilesRef.current = files;
            }

            // Set pending document IDs if provided
            if (documentIds && documentIds.length > 0) {
                pendingDocumentIdsRef.current = documentIds;
            }

            // Only open drawer if it's not already open AND we're not in dashboard view
            // This prevents opening the drawer when submitting from within the chat window in dashboard
            const isDashboardView = location.pathname.includes('/dashboard');
            const isAskEvePage = location.pathname.includes('/ask-eve');
            if (!isDrawerOpen && !isDashboardView && !isAskEvePage) {
                setIsDrawerOpen(true);
            }

            // Ensure conversation exists before sending message
            if (!backendConversationIdRef.current) {
                const convId = await ensureConversation();
                if (!convId) {
                    console.error("Cannot send message: conversation could not be created");
                    return;
                }
            }

            // Use append to directly add and send the message (more reliable than setInput + handleSubmit)
            // TODO: Pass documentIds to streamAppend when adapter supports it
            streamAppend({
                role: "user",
                content: text.trim(),
            });
        },
        [streamAppend, isDrawerOpen, location.pathname, backendConversationId, ensureConversation],
    );

    useEffect(() => {
        if (!isSenderOrReceiver) return;

        const onMouseMove = (e: MouseEvent) => {
            lastCursor.current = { x: e.clientX, y: e.clientY };
        };
        window.addEventListener("mousemove", onMouseMove, { passive: true });

        const isAIShortcut = (e: KeyboardEvent) => (e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === "a";
        const onKeyDown = (e: KeyboardEvent) => {
            if (isAIShortcut(e)) {
                e.preventDefault();
                const { x, y } = lastCursor.current;
                openFloatingPrompt({ x, y });
            }
        };
        window.addEventListener("keydown", onKeyDown);

        return () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [isSenderOrReceiver, openFloatingPrompt]);

    const restoreTabsFromStorage = useCallback(() => {
        try {
            const stored = storage.getItem(getTabsStorageKey());
            if (!stored) return false;
            const parsed = JSON.parse(stored) as { tabs?: ChatTab[]; activeChatId?: string | null };
            if (!parsed.tabs || parsed.tabs.length === 0) return false;

            const limitedTabs = parsed.tabs.slice(-10);
            setChatTabs(limitedTabs);
            setActiveChats(() => {
                const next = new Map<string, ChatSession>();
                limitedTabs.forEach((tab) => {
                    next.set(tab.id, {
                        id: tab.id,
                        backendConversationId: tab.backendConversationId,
                        title: tab.title,
                        messages: [],
                        createdAt: Date.now(),
                        lastMessageAt: null,
                        isLoaded: false,
                    });
                });
                return next;
            });

            const nextActiveId = parsed.activeChatId || limitedTabs[0].id;
            setCurrentChatId(nextActiveId);
            const activeTab = limitedTabs.find((tab) => tab.id === nextActiveId);
            if (activeTab?.backendConversationId) {
                backendConversationIdRef.current = activeTab.backendConversationId;
                setBackendConversationId(activeTab.backendConversationId);
                resetStreamState(true);
                loadConversationMessages(nextActiveId, activeTab.backendConversationId);
            }
            return true;
        } catch (error) {
            console.warn("Failed to restore chat tabs from storage:", error);
            return false;
        }
    }, [loadConversationMessages, resetStreamState]);

    useEffect(() => {
        if (!isAskEvePage) {
            // Reset ref when leaving Ask Eve page
            loadLatestChatCalledRef.current = false;
            return;
        }
        if (chatTabs.length > 0) {
            loadLatestChatCalledRef.current = true; // Mark as called if tabs exist
            return;
        }
        if (loadLatestChatCalledRef.current) return; // Already called

        const restored = restoreTabsFromStorage();
        if (!restored) {
            loadLatestChatCalledRef.current = true;
            loadLatestChat();
        } else {
            loadLatestChatCalledRef.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatTabs.length, isAskEvePage]);

    useEffect(() => {
        if (!isAskEvePage) return;
        try {
            const payload = {
                tabs: chatTabs,
                activeChatId: currentChatId,
            };
            storage.setItem(getTabsStorageKey(), JSON.stringify(payload));
        } catch (error) {
            console.warn("Failed to persist chat tabs:", error);
        }
    }, [chatTabs, currentChatId, isAskEvePage]);

    const isNewChat = useMemo(() => {
        return messages.length === 0 && !activeWizard;
    }, [messages.length, activeWizard]);

    // Track last messages to avoid unnecessary updates
    const lastMessagesRef = useRef<ChatMessage[]>([]);
    const lastChatIdRef = useRef<string | null>(null);

    useEffect(() => {
        if (!currentChatId) return;

        // Check if messages actually changed
        const messagesChanged =
            lastChatIdRef.current !== currentChatId ||
            messages.length !== lastMessagesRef.current.length ||
            messages.some((msg, idx) => {
                const lastMsg = lastMessagesRef.current[idx];
                return !lastMsg || lastMsg.id !== msg.id || lastMsg.content !== msg.content;
            });

        if (!messagesChanged) return;

        lastMessagesRef.current = messages;
        lastChatIdRef.current = currentChatId;

        setActiveChats((prev) => {
            const next = new Map(prev);
            const session = next.get(currentChatId);
            if (!session) return next;
            next.set(currentChatId, {
                ...session,
                messages,
                lastMessageAt: messages.length > 0 ? Date.now() : session.lastMessageAt,
            });
            return next;
        });
    }, [currentChatId, messages]);

    // Auto-save on unmount or when drawer closes
    useEffect(() => {
        return () => {
            if (currentChatId) {
                saveChatHistoryToStorage();
            }
        };
    }, [currentChatId, saveChatHistoryToStorage]);

    const value = useMemo<ChatAIContextValue>(
        () => ({
            isDrawerOpen,
            openDrawer,
            closeDrawer,
            toggleDrawer,
            isPromptOpen,
            promptPosition,
            openFloatingPrompt,
            closeFloatingPrompt,
            messages,
            appendMessage,
            submitPrompt,
            isTyping,
            startTyping: () => { }, // No-op, isTyping is now managed by useChatStream
            stopTyping: () => { }, // No-op, isTyping is now managed by useChatStream
            currentChatId,
            activeChats,
            activeChatId: currentChatId,
            isNewChat,
            createNewChat,
            saveChatHistory: saveChatHistoryToStorage,
            loadLastChat,
            getAllChats,
            loadChat,
            deleteChat,
            updateChatTitle,
            chatTabs,
            switchChat,
            closeChatTab,
            loadLatestChat,
            openConversation,
            isHistoryView,
            toggleHistoryView,
            activeWizard,
            setActiveWizard,
            wizardFormData,
            setWizardFormData,
            quoteRequestId,
            setQuoteRequestId,
            currentAgent,
            thinkingSteps,
            getThinkingStepsForMessage,
            activeToolCalls,
            agentDelegations,
            isStreaming,
            streamError,
            stop,
        }),
        [
            appendMessage,
            closeDrawer,
            closeFloatingPrompt,
            createNewChat,
            currentChatId,
            activeChats,
            deleteChat,
            getAllChats,
            isDrawerOpen,
            isHistoryView,
            isNewChat,
            isPromptOpen,
            isTyping,
            loadChat,
            loadLastChat,
            messages,
            chatTabs,
            switchChat,
            closeChatTab,
            loadLatestChat,
            openConversation,
            openDrawer,
            openFloatingPrompt,
            promptPosition,
            quoteRequestId,
            saveChatHistoryToStorage,
            submitPrompt,
            toggleDrawer,
            toggleHistoryView,
            updateChatTitle,
            activeWizard,
            setActiveWizard,
            wizardFormData,
            setWizardFormData,
            setQuoteRequestId,
            currentAgent,
            thinkingSteps,
            getThinkingStepsForMessage,
            activeToolCalls,
            agentDelegations,
            isStreaming,
            streamError,
            stop,
        ],
    );

    return <ChatAIContext.Provider value={value}>{children}</ChatAIContext.Provider>;
};

// Optional portals (drawer and prompt) will be mounted in main.tsx for clarity


